import type { Alignment } from '@floating-ui/dom'
import type { LitElement } from 'lit'
import type { LogicalSide } from '../positioning.js'
import { property } from 'lit/decorators.js'

type Constructor<T = Record<string, unknown>> = new (...args: any[]) => T

export declare class FloatingMixinInterface {
  open: boolean
  align: Alignment
  position: LogicalSide | 'auto'
  sideOffset: number
  alignOffset: number
}

export function FloatingMixin<T extends Constructor<LitElement>>(
  superClass: T,
): Constructor<FloatingMixinInterface> & T {
  class FloatingElement extends superClass {
    /**
     * Controls whether the component is open or not.
     */
    @property({ type: Boolean, reflect: true }) open = false

    /**
     * Set the alignment in relation to the toggle (or anchor) depending on the position.
     * `start` will align it to the left of the toggle (or anchor).
     * `end` will align it to the right of the toggle (or anchor).
     * Setting the `position` to `inline-start` or `inline-end` will switch
     * `start` and `end` to the top and bottom respectively.
     */
    @property({ reflect: true }) align: 'start' | 'end' = 'start'

    /**
     * Set the position in relation to the toggle (or anchor).
     * Options follow logical properties.
     * `block-start` and `block-end` referring to top and bottom respectively,
     * `inline-start` and `inline-end` referring to left and right respectively.
     * You can also set it to `auto` for automatic positioning depending on
     * which side has the most space available.
     */
    @property({ reflect: true }) position: 'block-end' | 'block-start' | 'inline-start' | 'inline-end' | 'auto'
      = 'block-end'

    /**
     * Distance in pixels from the toggle (or anchor) along the main axis — the
     * gap between the floating element and the side it opens against. Fed into
     * Floating UI's `offset` middleware as `mainAxis`. Defaults to `8`.
     */
    @property({ reflect: true, type: Number, attribute: 'side-offset' }) sideOffset = 8

    /**
     * Offset in pixels along the alignment axis — slides the floating element
     * toward the `start` or `end` of the toggle (or anchor). Fed into Floating
     * UI's `offset` middleware as `alignmentAxis`. Defaults to `0`.
     */
    @property({ reflect: true, type: Number, attribute: 'align-offset' }) alignOffset = 0
  }

  return FloatingElement
}
